/**
 * PresentationFilePreview React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

// Material UI
import Typography from '@material-ui/core/Typography';

// application components
import PresentationFileView from "../presentation/kubus/PresentationFileView";
import PresentationAbstractView from "../presentation/kubus/PresentationAbstractView";
import PageSelection from "../base/PageSelection";
import ViewWidthScaling from "../base/ViewWidthScaling";

// application
import {selectObjectsForIds} from "../../redux/editing/selectors";
import {selectBaseStylesheet} from "../../redux/configuration/selectors";


/**
 * preview for presentation files
 *
 * @author Jens Gruschel
 */
class PresentationFilePreview extends React.Component {

    state = {
        modeIndex: 2,
        pageIndex: 0
    }

    render() {

        if (!this.props.visible) return null;

        const width = this.props.isTop ? 1080 : 300;
        const height = width * 2 * 16 / 9;

        if (this.props.isOverview) {

            return <div>
                {this.props.baseStylesheet && <style>
                    {this.props.baseStylesheet}
                </style>}
                <ViewWidthScaling width={width} height={height}>
                    {this.state.modeIndex === 1 ? <PresentationAbstractView
                        presentationFileOrFolder={this.props.object}
                        findReferencedObjects={this.props.findReferencedObjects}
                    /> : <PresentationFileView
                        presentationFile={this.props.object}
                        findReferencedObjects={this.props.findReferencedObjects}
                    />}
                </ViewWidthScaling>
                <PageSelection
                    pageNumber={this.state.modeIndex}
                    pageCount={2}
                    onChange={(number) => this.setState({modeIndex: number})}
                    onLabel={(number) => <Typography variant="button">
                        {number === 1 ? "Kurzinfo": "Übersicht"}
                    </Typography>}
                />
            </div>;
        }
        else {

            const includingCover = this.props.isTop;
            const objectFields = this.props.object.fields || {};
            const chapters = objectFields.chapters || [];
            const chapterCount = chapters.length;
            const pageCount = includingCover ? chapterCount + 1 : chapterCount;
            const pageIndex = Math.min(this.state.pageIndex, pageCount - 1);

            return <div>
                {this.props.baseStylesheet && <style>
                    {this.props.baseStylesheet}
                </style>}
                <ViewWidthScaling width={width} height={height}>
                    <PresentationFileView
                        presentationFile={this.props.object}
                        navigationIndex={[includingCover ? pageIndex : pageIndex + 1, 0]}
                        findReferencedObjects={this.props.findReferencedObjects}
                    />
                </ViewWidthScaling>
                {pageCount > 0 && <PageSelection
                    pageNumber={pageIndex + 1}
                    pageCount={pageCount}
                    onChange={(number) => this.setState({pageIndex: number - 1})}
                    onLabel={(number, count) => <Typography variant="button">
                        {includingCover ? (number === 1 ? "Übersicht": "Kapitel " + (number - 1) + "/" + (count - 1)) : number + "/" + count}
                    </Typography>}
                />}
            </div>;
        }
    }
}

PresentationFilePreview.propTypes = {
    place: PropTypes.string.isRequired,
    fieldSet: PropTypes.object.isRequired,
    objectType: PropTypes.string,
    objectId: PropTypes.string,
    object: PropTypes.object,
    readOnly: PropTypes.bool,
    onObjectValueChange: PropTypes.func.isRequired
};

const mapStateToProps = (state, props) => {

    const isTop = props.place === "top";
    const visible = isTop ? props.fieldSet.id === "preview" : ["general", "content"].includes(props.fieldSet.id);
    const isOverview = props.fieldSet.id === "general";

    return {
        isTop: isTop,
        visible: visible,
        isOverview: isOverview,
        baseStylesheet: selectBaseStylesheet(state.configuration),
        findReferencedObjects: field => selectObjectsForIds(state.editing, field)
    }
}

export default connect(mapStateToProps)(PresentationFilePreview);